import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';

export interface AuthUser {
  subject: string;
  email: string;
  name?: string | undefined;
  hostedDomain: string;
}

interface AuthSession {
  configured: boolean;
  user: AuthUser | null;
}

interface AuthContextValue extends AuthSession {
  isLoading: boolean;
  hasError: boolean;
  canMutate: boolean;
  signInUrl: (returnTo: string) => string;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const isLive = import.meta.env.VITE_API_MODE === 'live';

async function loadSession(): Promise<AuthSession> {
  const response = await fetch('/api/v1/whoami', {
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Could not load the current sign-in session');
  return (await response.json()) as AuthSession;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: ['auth-session'],
    queryFn: loadSession,
    enabled: isLive,
    retry: false,
    staleTime: 60_000,
  });

  const configured = isLive ? Boolean(session.data?.configured) : false;
  const hasError = isLive && session.isError;
  const user = isLive
    ? (session.data?.user ?? null)
    : {
        subject: 'mock-user',
        email: 'mock.user@tidalx.ai',
        name: 'Mock user',
        hostedDomain: 'tidalx.ai',
      };

  async function signOut(): Promise<void> {
    if (!isLive) return;
    await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'same-origin' });
    await queryClient.invalidateQueries({ queryKey: ['auth-session'] });
  }

  return (
    <AuthContext.Provider
      value={{
        configured,
        user,
        isLoading: isLive && session.isLoading,
        hasError,
        canMutate: !isLive || (!hasError && (!configured || Boolean(user))),
        signInUrl: (returnTo) =>
          `/api/auth/google?returnTo=${encodeURIComponent(
            returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/',
          )}`,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
